import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { execMeetingAPI } from '../../api/client';
import { COLORS, RADIUS, SHADOW, badge, btn, card, input as inputStyle } from '../../utils/uiStyles';
import { RichViewer } from './RichEditor';
import UnifiedPrintView, { PRINT_READY_TIMEOUT_MS, waitForPrintReady } from './UnifiedPrintView';
import { DEPT_ALIAS, sortByDeptOrder } from './execMeetingConst';

// 제출 현황 기준 부서 — 관리자 탭의 부서 목록(사용 중인 것 · GET /exec-meeting/departments)이 정본이다(#3130).
//   이 상수는 목록을 못 불러올 때(실패·배열 아님·빈 배열)의 대체값이다.
const BASELINE_DEPTS = [
  '영업1팀',
  '영업2팀',
  '기획팀',
  '경영혁신팀',
  '신센터TF',
  '일동제약컨설팅TF',
  'AX실',
  '경영지원팀',
  '물류실',
];

// 부서 API 응답 → 이름 목록(순서 유지 · 빈 이름·중복 제거) — SubmitTab.jsx 에 같은 두 함수가 있다(한쪽을 고치면 같이 고친다).
function deptNames(data) {
  if (!Array.isArray(data)) return [];
  const seen = new Set();
  return data
    .map((item) => (typeof item === 'string' ? item : item?.name))
    .filter((name) => typeof name === 'string' && name.trim() && !seen.has(name) && seen.add(name));
}

// 🔴 Promise 로 감싸 부른다 — departments 가 없는 가짜 API(옛 시험)에서도 동기 TypeError 로 화면이 죽지 않고 대체값으로 떨어진다.
function loadDeptNames() {
  return Promise.resolve()
    .then(() => execMeetingAPI.departments())
    .then((response) => deptNames(response?.data));
}
const MAX_DETAIL_REQUESTS = 4;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MAX_LENGTH = 50;
const EMPTY_SEARCH = { status: 'idle', query: '', weeks: [], hits: [] };
const MAX_KPIS = 100;
const MAX_KPI_TEXT = 255;
const THEMES = [
  { number: 1, title: 't1_title', items: [['t1i1_label', 't1i1'], ['t1i2_label', 't1i2']] },
  { number: 2, title: 't2_title', items: [['t2i1_label', 't2i1'], ['t2i2_label', 't2i2']] },
  { number: 3, title: 't3_title', items: [['t3i1_label', 't3i1'], ['t3i2_label', 't3i2']] },
];

const labelStyle = { display: 'block', marginBottom: '6px', color: COLORS.textSub, fontSize: '12px', fontWeight: 700 };

function canonicalDept(dept) {
  return DEPT_ALIAS[dept] || dept;
}

function sortReports(reports, baselineDepts = BASELINE_DEPTS) {
  const baselineOrder = new Map(baselineDepts.map((dept, index) => [dept, index]));
  return sortByDeptOrder(reports, (report) => report.dept)
    .map((report, index) => ({ report, index }))
    .sort((left, right) => {
      const leftOrder = baselineOrder.get(canonicalDept(left.report.dept)) ?? 999;
      const rightOrder = baselineOrder.get(canonicalDept(right.report.dept)) ?? 999;
      return leftOrder - rightOrder || left.index - right.index;
    })
    .map(({ report }) => report);
}

function createDetailScheduler() {
  let active = 0;
  const pending = [];

  function drain() {
    while (active < MAX_DETAIL_REQUESTS && pending.length) {
      const request = pending.shift();
      if (!request.isCurrent()) {
        request.resolve(null);
        continue;
      }
      active += 1;
      execMeetingAPI.get(request.id)
        .then((response) => request.resolve(response.data || {}))
        .catch(request.reject)
        .finally(() => {
          active -= 1;
          drain();
        });
    }
  }

  return {
    load(id, isCurrent) {
      return new Promise((resolve, reject) => {
        pending.push({ id, isCurrent, resolve, reject });
        drain();
      });
    },
    discardStale: drain,
  };
}

// 검색 결과 글자에서 검색어만 굵게 — 🔴 서버 snippet 은 «<»·«>» 가 풀린 평문이라 React 텍스트 노드로만 그린다
//   (dangerouslySetInnerHTML·innerHTML·문자열 치환 금지 · 2차 검토 H2).
function highlightQuery(text, query) {
  const source = String(text ?? '');
  const needle = String(query ?? '').trim().split(/\s+/).join(' ').toLowerCase();
  if (!needle) return source;
  const lower = source.toLowerCase();
  const parts = [];
  let from = 0;
  let index = lower.indexOf(needle, from);
  while (index !== -1) {
    if (index > from) parts.push(source.slice(from, index));
    parts.push(<strong key={index}>{source.slice(index, index + needle.length)}</strong>);
    from = index + needle.length;
    index = lower.indexOf(needle, from);
  }
  if (from < source.length) parts.push(source.slice(from));
  return parts;
}

function boundedKpiText(value) {
  try {
    return String(value ?? '').slice(0, MAX_KPI_TEXT);
  } catch {
    return '';
  }
}

function normalizedKpis(value) {
  if (!Array.isArray(value)) return [];
  return value.slice(0, MAX_KPIS).map((item) => ({
    label: boundedKpiText(item?.label),
    value: boundedKpiText(item?.value),
    unit: boundedKpiText(item?.unit),
  }));
}

function ReportContent({ report, onDownload, downloadingFileId }) {
  const dept = report.dept || '부서 미지정';
  const kpis = normalizedKpis(report.kpis);
  const files = Array.isArray(report.files) ? report.files : [];

  return (
    <>
      {THEMES.map((theme) => (
        <section key={theme.number} style={{ marginTop: '18px', paddingTop: '16px', borderTop: `1px solid ${COLORS.borderSoft}` }}>
          <h4 style={{ margin: '0 0 12px', color: COLORS.text, fontSize: '15px' }}>
            테마 {theme.number}{report[theme.title] ? ` · ${report[theme.title]}` : ''}
          </h4>
          {theme.items.map(([labelField, richField], index) => (
            <div key={richField} style={{ marginTop: index ? '14px' : 0 }}>
              <div style={labelStyle}>{report[labelField] || `항목 ${index + 1}`}</div>
              <RichViewer initialHTML={report[richField] || ''} ariaLabel={`${dept} 테마 ${theme.number} 항목 ${index + 1} 본문`} />
            </div>
          ))}
        </section>
      ))}

      {[['ai_usage', 'AI 활용'], ['etc', '기타']].map(([field, title]) => (
        <section key={field} style={{ marginTop: '18px', paddingTop: '16px', borderTop: `1px solid ${COLORS.borderSoft}` }}>
          <h4 style={{ margin: '0 0 10px', color: COLORS.text, fontSize: '15px' }}>{title}</h4>
          <RichViewer initialHTML={report[field] || ''} ariaLabel={`${dept} ${title} 본문`} />
        </section>
      ))}

      <section style={{ marginTop: '18px', paddingTop: '16px', borderTop: `1px solid ${COLORS.borderSoft}` }}>
        <h4 style={{ margin: '0 0 10px', color: COLORS.text, fontSize: '15px' }}>KPI</h4>
        {!kpis.length ? (
          <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>등록된 KPI가 없습니다.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table aria-label={`${dept} KPI`} style={{ width: '100%', minWidth: '420px', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  {['지표', '값', '단위'].map((value) => <th key={value} scope="col" style={{ padding: '8px', textAlign: 'left', borderBottom: `1px solid ${COLORS.border}` }}>{value}</th>)}
                </tr>
              </thead>
              <tbody>
                {kpis.map((kpi, index) => (
                  <tr key={index}>
                    <td style={{ padding: '8px', borderBottom: `1px solid ${COLORS.borderSoft}` }}>{kpi.label}</td>
                    <td style={{ padding: '8px', borderBottom: `1px solid ${COLORS.borderSoft}` }}>{kpi.value}</td>
                    <td style={{ padding: '8px', borderBottom: `1px solid ${COLORS.borderSoft}` }}>{kpi.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={{ marginTop: '18px', paddingTop: '16px', borderTop: `1px solid ${COLORS.borderSoft}` }}>
        <h4 style={{ margin: '0 0 10px', color: COLORS.text, fontSize: '15px' }}>첨부</h4>
        {!files.length ? (
          <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>첨부 파일이 없습니다.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {files.map((file) => (
              <li key={file.id || file.name} style={{ marginTop: '5px' }}>
                <button
                  type="button"
                  aria-label={`첨부파일 다운로드: ${file.name || 'download'}`}
                  disabled={!file.id || downloadingFileId !== null}
                  onClick={() => onDownload(file)}
                  style={{ padding: 0, border: 0, background: 'transparent', color: COLORS.primaryDark, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {downloadingFileId === file.id ? '다운로드 중…' : file.name || 'download'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function ReportCard({ report, onRetry, onDownload, downloadingFileId }) {
  const dept = report.dept || '부서 미지정';
  return (
    <article aria-label={`${dept} 통합 보고서`} style={{ ...card, marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: COLORS.text, fontSize: '17px' }}>{dept}</h3>
        <span style={badge(report.detailError ? 'danger' : 'success')}>{report.detailError ? '상세 조회 실패' : '제출'}</span>
      </div>
      <div style={{ marginTop: '8px', color: COLORS.textSub, fontSize: '13px' }}>작성자: {report.author || '미입력'}</div>
      {report.detailLoading && (
        <div role="status" style={{ marginTop: '12px', color: COLORS.textSub, fontSize: '13px' }}>상세 보고서를 불러오는 중입니다.</div>
      )}
      {report.detailError && (
        <div style={{ marginTop: '12px' }}>
          <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px' }}>상세 보고서를 불러오지 못했습니다.</div>
          <button type="button" onClick={() => onRetry(report)} style={{ marginTop: '8px' }}>다시 불러오기</button>
        </div>
      )}
      {report.detailLoaded && (
        <ReportContent report={report} onDownload={onDownload} downloadingFileId={downloadingFileId} />
      )}
    </article>
  );
}

// printReadyTimeoutMs — PDF 준비 대기 상한(시험에서만 짧게 준다).
export default function UnifiedTab({ printReadyTimeoutMs = PRINT_READY_TIMEOUT_MS }) {
  const [weeks, setWeeks] = useState([]);
  const [weekKey, setWeekKey] = useState('');
  const [reports, setReports] = useState([]);
  const [loadingWeeks, setLoadingWeeks] = useState(true);
  const [loadingReports, setLoadingReports] = useState(false);
  const [weeksError, setWeeksError] = useState(false);
  const [listError, setListError] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [baselineDepts, setBaselineDepts] = useState(BASELINE_DEPTS);
  // 검색바(#3130 4번)
  const [searchText, setSearchText] = useState('');
  const [searchResult, setSearchResult] = useState(EMPTY_SEARCH);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // PDF 저장(#3130 1번) — {id, weekKey, label, reports, pending}. 있으면 인쇄 보기를 body 포털로 마운트한다.
  const [printJob, setPrintJob] = useState(null);
  const baselineRef = useRef(BASELINE_DEPTS);
  const requestGenerationRef = useRef(0);
  const downloadRequestRef = useRef(0);
  const detailSchedulerRef = useRef(null);
  const searchSeqRef = useRef(0);
  const pendingScrollRef = useRef(null);
  const printSeqRef = useRef(0);
  const activePrintRef = useRef(null);
  const startedPrintRef = useRef(null);
  if (detailSchedulerRef.current === null) detailSchedulerRef.current = createDetailScheduler();

  const selectWeek = useCallback((nextWeek) => {
    requestGenerationRef.current += 1;
    downloadRequestRef.current += 1;
    detailSchedulerRef.current.discardStale();
    // 주차가 바뀌면 준비 중인 PDF 는 취소한다(print 를 부르지 않는다).
    activePrintRef.current = null;
    setPrintJob(null);
    setWeekKey(nextWeek);
    setReports([]);
    setListError(false);
    setDownloadError('');
    setDownloadingFileId(null);
    setLoadingReports(Boolean(nextWeek));
  }, []);

  const loadDetail = useCallback(async (report, generation = requestGenerationRef.current) => {
    const isCurrent = () => requestGenerationRef.current === generation;
    setReports((current) => current.map((item) => (
      item.id === report.id ? { ...item, detailError: false, detailLoading: true } : item
    )));
    try {
      const detail = await detailSchedulerRef.current.load(report.id, isCurrent);
      if (!detail || !isCurrent()) return;
      setReports((current) => current.map((item) => (
        item.id === report.id
          ? { ...item, ...detail, id: item.id, detailError: false, detailLoaded: true, detailLoading: false }
          : item
      )));
    } catch {
      if (!isCurrent()) return;
      setReports((current) => current.map((item) => (
        item.id === report.id
          ? { ...item, detailError: true, detailLoaded: false, detailLoading: false }
          : item
      )));
    }
  }, []);

  useEffect(() => {
    let alive = true;
    setLoadingWeeks(true);
    execMeetingAPI.weeks()
      .then((response) => {
        if (!alive) return;
        const items = Array.isArray(response.data) ? response.data : [];
        setWeeks(items);
        const selected = items.find((item) => item.is_current) || items[0];
        if (selected) selectWeek(selected.key);
      })
      .catch(() => {
        if (alive) setWeeksError(true);
      })
      .finally(() => {
        if (alive) setLoadingWeeks(false);
      });
    return () => { alive = false; };
  }, [selectWeek]);

  // 제출 현황 기준 부서 = 관리자 탭의 부서 목록. 실패·빈 목록이면 BASELINE_DEPTS 그대로.
  useEffect(() => {
    let alive = true;
    loadDeptNames()
      .then((names) => {
        if (!alive || !names.length) return;
        baselineRef.current = names;
        setBaselineDepts(names);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => () => {
    requestGenerationRef.current += 1;
    downloadRequestRef.current += 1;
    detailSchedulerRef.current.discardStale();
    // 탭 전환(언마운트) — 늦은 검색 응답은 버리고, 준비 중인 PDF 는 print 를 부르지 않는다.
    searchSeqRef.current += 1;
    activePrintRef.current = null;
  }, []);

  // 검색 — 입력이 300ms 멈추면 부른다. 🔴 Promise 로 감싸 부른다(search 가 없는 가짜 API 에서도 동기 오류가 안 나게).
  //   늦게 온 응답은 순번(searchSeqRef)으로 버린다.
  useEffect(() => {
    const query = searchText.trim();
    searchSeqRef.current += 1;
    const seq = searchSeqRef.current;
    if (!query) {
      setSearchResult(EMPTY_SEARCH);
      setActiveIndex(-1);
      return undefined;
    }
    // 지난 검색어의 결과를 바로 지운다(멈추기 전 Enter 가 옛 결과를 고르지 않게).
    setSearchResult({ status: 'loading', query, weeks: [], hits: [] });
    setActiveIndex(-1);
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(() => execMeetingAPI.search(query))
        .then((response) => {
          if (searchSeqRef.current !== seq) return;
          const data = response?.data || {};
          setSearchResult({
            status: 'done',
            query,
            weeks: Array.isArray(data.weeks) ? data.weeks : [],
            hits: Array.isArray(data.hits) ? data.hits : [],
          });
        })
        .catch(() => {
          if (searchSeqRef.current === seq) setSearchResult({ status: 'error', query, weeks: [], hits: [] });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!weekKey) return undefined;
    let cancelled = false;
    const generation = requestGenerationRef.current;
    const isCurrent = () => !cancelled && requestGenerationRef.current === generation;

    execMeetingAPI.list(weekKey)
      .then((response) => {
        if (!isCurrent()) return;
        const summaries = sortReports(Array.isArray(response.data) ? response.data : [], baselineRef.current);
        setReports(summaries.map((report) => ({
          ...report,
          detailError: false,
          detailLoaded: false,
          detailLoading: true,
        })));
        summaries.forEach((report) => loadDetail(report, generation));
      })
      .catch(() => {
        if (isCurrent()) setListError(true);
      })
      .finally(() => {
        if (isCurrent()) setLoadingReports(false);
      });

    return () => { cancelled = true; };
  }, [loadDetail, weekKey]);

  const statusRows = useMemo(() => {
    const standard = baselineDepts.map((dept) => ({
      dept,
      reports: reports.filter((report) => canonicalDept(report.dept) === dept),
    }));
    const extras = reports
      .filter((report) => !baselineDepts.includes(canonicalDept(report.dept)))
      .map((report) => ({ dept: report.dept || '부서 미지정', reports: [report] }));
    return [...standard, ...extras];
  }, [reports, baselineDepts]);
  // 부서 목록이 보고서 목록보다 늦게 와도 순서가 목록을 따르게 그릴 때 다시 정렬한다(같은 기준이면 순서 그대로).
  const orderedReports = useMemo(() => sortReports(reports, baselineDepts), [reports, baselineDepts]);
  const submittedCount = statusRows.filter((row) => row.reports.length > 0).length;
  const partialFailureCount = reports.filter((report) => report.detailError).length;

  // 본문 검색 결과로 고른 부서 카드로 스크롤 — 대상 카드와 그 앞 카드들의 상세가 «받음 또는 실패»로 끝나고
  //   요소를 찾았을 때만 대상을 비운다(jsdom 엔 scrollIntoView 가 없어 ?. 필수).
  const flushPendingScroll = useCallback((list, loading) => {
    const target = pendingScrollRef.current;
    if (target === null || loading) return;
    const index = list.findIndex((report) => report.id === target);
    if (index < 0) return;
    if (!list.slice(0, index + 1).every((report) => report.detailLoaded || report.detailError)) return;
    const element = document.getElementById(`exec-report-${target}`);
    if (!element) return;
    pendingScrollRef.current = null;
    element.scrollIntoView?.({ block: 'start' });
  }, []);

  useEffect(() => {
    flushPendingScroll(orderedReports, loadingReports);
  }, [flushPendingScroll, orderedReports, loadingReports]);

  const weekIndex = weeks.findIndex((week) => week.key === weekKey);
  const previousWeek = weekIndex > 0 ? weeks[weekIndex - 1] : null;
  const nextWeek = weekIndex >= 0 && weekIndex < weeks.length - 1 ? weeks[weekIndex + 1] : null;

  const goToWeek = (week) => {
    if (!week?.key) return;
    // 🔴 지금 주차면 selectWeek 를 부르지 않는다 — 같은 키면 목록 effect 가 안 돌아 «불러오는 중»에서 멈춘다.
    if (week.key === weekKey) return;
    // 주차 목록에 없는 주차(검색이 계산한 주차)는 목록에 합쳐 정렬한 뒤 고른다 — select 에 option 이 있어야 라벨이 맞는다.
    setWeeks((current) => (current.some((item) => item.key === week.key)
      ? current
      : [...current, { key: week.key, label: week.label || week.key, is_current: Boolean(week.is_current) }]
        .sort((left, right) => (left.key < right.key ? -1 : left.key > right.key ? 1 : 0))));
    selectWeek(week.key);
  };

  const searchItems = useMemo(() => [
    ...searchResult.weeks.map((week) => ({ type: 'week', week })),
    ...searchResult.hits.map((hit) => ({ type: 'hit', hit })),
  ], [searchResult]);

  const chooseSearchItem = (item) => {
    if (!item) return;
    setSearchOpen(false);
    setActiveIndex(-1);
    if (item.type === 'week') {
      goToWeek(item.week);
      return;
    }
    const { hit } = item;
    if (hit?.report_id === undefined || hit?.report_id === null) return;
    pendingScrollRef.current = hit.report_id;
    if (hit.week_key === weekKey) {
      // 같은 주차 — reports 가 안 바뀌어 effect 가 안 돈다. 지금 바로 스크롤한다(아직 준비 전이면 effect 가 이어받는다).
      flushPendingScroll(orderedReports, loadingReports);
      return;
    }
    goToWeek({ key: hit.week_key, label: hit.week_label });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      setSearchOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!searchItems.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSearchOpen(true);
      setActiveIndex((index) => (index + 1) % searchItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSearchOpen(true);
      setActiveIndex((index) => (index <= 0 ? searchItems.length - 1 : index - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      chooseSearchItem(searchItems[activeIndex >= 0 && activeIndex < searchItems.length ? activeIndex : 0]);
    }
  };

  // PDF 저장 — 못 누르는 이유(빈 문자열이면 누를 수 있다).
  const printBlocker = (() => {
    if (loadingWeeks || loadingReports) return '주차·보고서를 불러오는 중입니다';
    if (!weekKey) return '선택한 주차가 없습니다';
    if (listError) return '보고서 목록을 불러오지 못했습니다';
    if (!reports.length) return '제출된 보고서가 없습니다';
    if (reports.some((report) => report.detailError)) return '상세를 못 받은 부서가 있습니다 — 다시 시도 뒤 저장';
    if (reports.some((report) => !report.detailLoaded)) return '상세 보고서를 불러오는 중입니다';
    return '';
  })();
  const printing = printJob !== null;

  const endPrint = (id) => {
    if (activePrintRef.current === id) activePrintRef.current = null;
    setPrintJob((current) => (current && current.id === id ? null : current));
  };

  // 🔴 afterprint 에 기대지 않는다 — print() 를 부른 try/finally 안에서 제목 복원·언마운트(Chrome·Edge 는 print 가 창이 닫힐 때까지 막는다).
  const printNow = (job) => {
    if (activePrintRef.current !== job.id) return;
    const previousTitle = document.title;
    try {
      document.title = `통합 주간 보고서_${job.weekKey}`;
      window.print();
    } finally {
      document.title = previousTitle;
      endPrint(job.id);
    }
  };

  // 인쇄 보기가 마운트돼 루트 요소를 넘겨준 뒤에야 준비 대기를 시작한다(포털은 클릭한 그 틱엔 DOM 이 없다).
  const handlePrintRoot = async (root, job) => {
    if (startedPrintRef.current === job.id) return;
    startedPrintRef.current = job.id;
    const isActive = () => activePrintRef.current === job.id;
    let result;
    try {
      result = await waitForPrintReady(root, { timeoutMs: printReadyTimeoutMs, isCancelled: () => !isActive() });
    } catch {
      endPrint(job.id);
      return;
    }
    if (result.cancelled || !isActive()) return;
    if (result.ready) {
      printNow(job);
      return;
    }
    // 상한 초과 — 인쇄하지 않고 묻는다(그대로 찍으면 «본문을 불러오는 중입니다.»가 PDF 에 남는다). 묻는 동안 포털은 유지.
    setPrintJob((current) => (current && current.id === job.id ? { ...current, pending: result.pending } : current));
  };

  const startPrint = () => {
    if (printBlocker || printJob) return;
    printSeqRef.current += 1;
    const id = printSeqRef.current;
    activePrintRef.current = id;
    // 누른 순간의 스냅숏 — 제출된 부서만(보고서 목록 = 제출분) · 통합 탭 카드 순서.
    setPrintJob({
      id,
      weekKey,
      label: weeks.find((week) => week.key === weekKey)?.label || weekKey,
      reports: orderedReports.filter((report) => report.detailLoaded),
      pending: null,
    });
  };

  const downloadFile = async (file) => {
    const generation = requestGenerationRef.current;
    const requestId = downloadRequestRef.current + 1;
    downloadRequestRef.current = requestId;
    let objectURL = null;
    setDownloadingFileId(file.id);
    setDownloadError('');
    try {
      const response = await execMeetingAPI.downloadFile(file.id);
      if (requestGenerationRef.current !== generation || downloadRequestRef.current !== requestId) return;
      objectURL = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = objectURL;
      anchor.download = file.name || 'download';
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
      }
    } catch (error) {
      if (requestGenerationRef.current !== generation || downloadRequestRef.current !== requestId) return;
      setDownloadError(error.response?.status === 410
        ? '원본 파일이 없어 다운로드할 수 없습니다.'
        : '첨부 파일을 다운로드하지 못했습니다.');
    } finally {
      if (objectURL) URL.revokeObjectURL(objectURL);
      if (downloadRequestRef.current === requestId) setDownloadingFileId(null);
    }
  };

  return (
    <div>
      {weeksError && <div role="alert" style={{ marginBottom: '14px', color: COLORS.dangerText }}>주차 목록을 불러오지 못했습니다.</div>}
      {downloadError && <div role="alert" style={{ marginBottom: '14px', color: COLORS.dangerText }}>{downloadError}</div>}

      <section style={{ ...card, marginBottom: '14px' }}>
        <label style={{ display: 'block', maxWidth: '360px' }}>
          <span style={labelStyle}>주차</span>
          <select aria-label="통합 보고서 주차" value={weekKey} disabled={loadingWeeks || !weeks.length} onChange={(event) => selectWeek(event.target.value)} style={inputStyle}>
            {!weeks.length && <option value="">선택할 주차가 없습니다</option>}
            {weeks.map((week) => <option key={week.key} value={week.key}>{week.label}</option>)}
          </select>
        </label>
        {/* 검색바(#3130 4번) — 기존 주차 label 밖 · 다른 이름(«통합 보고서 검색»). 🔴 role="status"·aria-live 를 두지 않는다(r6 시험이 문서 전체에서 status 1개를 요구). */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px' }}>
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 0, maxWidth: '480px' }}>
            <input
              type="text"
              aria-label="통합 보고서 검색"
              aria-autocomplete="list"
              aria-controls="exec-unified-search-results"
              aria-activedescendant={searchOpen && activeIndex >= 0 ? `exec-unified-search-option-${activeIndex}` : undefined}
              placeholder="주차·날짜·낱말로 찾기 (예: 44주차, 10/27, 빙그레)"
              value={searchText}
              maxLength={SEARCH_MAX_LENGTH}
              disabled={printing}
              onChange={(event) => {
                setSearchText(event.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setSearchOpen(false)}
              onKeyDown={handleSearchKeyDown}
              style={inputStyle}
            />
            {searchOpen && searchResult.status !== 'idle' && (
              <div
                style={{ position: 'absolute', zIndex: 20, top: 'calc(100% + 4px)', left: 0, right: 0, maxHeight: '360px', overflowY: 'auto', padding: '6px 0', background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, boxShadow: SHADOW.float, fontSize: '13px' }}
              >
                {searchResult.status === 'loading' && <div style={{ padding: '8px 12px', color: COLORS.textSub }}>찾는 중…</div>}
                {searchResult.status === 'error' && <div style={{ padding: '8px 12px', color: COLORS.dangerText }}>검색에 실패했습니다</div>}
                {searchResult.status === 'done' && !searchItems.length && <div style={{ padding: '8px 12px', color: COLORS.textMuted }}>찾은 결과가 없습니다</div>}
                {searchResult.status === 'done' && searchItems.length > 0 && (
                  <ul id="exec-unified-search-results" role="listbox" aria-label="통합 보고서 검색 결과" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {[['week', '주차'], ['hit', '본문']].map(([type, title]) => {
                      const entries = searchItems
                        .map((item, index) => ({ item, index }))
                        .filter(({ item }) => item.type === type);
                      if (!entries.length) return null;
                      return (
                        <li key={type} role="presentation">
                          <div aria-hidden="true" style={{ padding: '6px 12px 2px', color: COLORS.textMuted, fontSize: '11px', fontWeight: 700 }}>{title}</div>
                          <ul role="group" aria-label={title} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {entries.map(({ item, index }) => (
                              <li
                                key={index}
                                id={`exec-unified-search-option-${index}`}
                                role="option"
                                aria-selected={index === activeIndex}
                                onMouseDown={(event) => event.preventDefault()}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => chooseSearchItem(item)}
                                style={{ padding: '7px 12px', cursor: 'pointer', color: COLORS.text, background: index === activeIndex ? COLORS.primarySoft : 'transparent', overflowWrap: 'anywhere' }}
                              >
                                {item.type === 'week'
                                  ? highlightQuery(item.week.label || item.week.key, searchResult.query)
                                  : (
                                    <>
                                      <span style={{ color: COLORS.textSub }}>{`${item.hit.week_label || item.hit.week_key} · ${item.hit.dept || '부서 미지정'} · `}</span>
                                      {highlightQuery(item.hit.snippet, searchResult.query)}
                                    </>
                                  )}
                              </li>
                            ))}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
          <button type="button" aria-label="이전 주" disabled={!previousWeek || printing || loadingWeeks} onClick={() => goToWeek(previousWeek)} style={btn('secondary')}>◀ 이전 주</button>
          <button type="button" aria-label="다음 주" disabled={!nextWeek || printing || loadingWeeks} onClick={() => goToWeek(nextWeek)} style={btn('secondary')}>다음 주 ▶</button>
          <button
            type="button"
            disabled={Boolean(printBlocker) || printing}
            title={printing ? undefined : printBlocker || '선택한 주차를 가로 A4 로 인쇄합니다 — 인쇄 창에서 «PDF로 저장»을 고르세요'}
            onClick={startPrint}
            style={btn('primary')}
          >
            {printing ? 'PDF 준비 중…' : 'PDF 저장'}
          </button>
        </div>
        {printJob && printJob.pending !== null && (
          <div role="alert" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px', color: COLORS.warn, fontSize: '13px' }}>
            <span>그림 {printJob.pending}개를 아직 못 받았습니다</span>
            <button type="button" onClick={() => printNow(printJob)} style={btn('secondary', 'sm')}>그래도 저장</button>
            <button type="button" onClick={() => endPrint(printJob.id)} style={btn('quiet', 'sm')}>취소</button>
          </div>
        )}
      </section>

      {weekKey && (
        <section aria-labelledby="submission-status-heading" style={{ ...card, marginBottom: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <h3 id="submission-status-heading" style={{ margin: 0, color: COLORS.text, fontSize: '16px' }}>제출 현황</h3>
            <div aria-label="제출 현황 합계" style={{ color: COLORS.textSub, fontSize: '13px' }}>
              {loadingReports
                ? '제출 현황을 불러오는 중입니다.'
                : listError
                  ? '제출 현황을 확인하지 못했습니다.'
                  : `제출 ${submittedCount} · 미제출 ${statusRows.length - submittedCount} · 전체 ${statusRows.length}`}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
            {statusRows.map((row) => {
              const submitted = row.reports.length > 0;
              const actualNames = [...new Set(row.reports.map((report) => report.dept).filter((dept) => dept && dept !== row.dept))];
              return (
                <div key={row.dept} style={{ padding: '9px 10px', border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, background: submitted ? COLORS.successSoft : COLORS.surface }}>
                  <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>{row.dept}</div>
                  <div style={{ marginTop: '3px', color: submitted ? COLORS.successText : COLORS.textMuted, fontSize: '12px' }}>
                    {loadingReports ? '확인 중' : listError ? '확인 실패' : submitted ? '제출' : '미제출'}
                  </div>
                  {actualNames.length > 0 && <div style={{ marginTop: '2px', color: COLORS.textSub, fontSize: '11px' }}>저장 부서명: {actualNames.join(', ')}</div>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {loadingReports && <div role="status" style={{ padding: '20px 0', color: COLORS.textSub }}>통합 보고서를 불러오는 중입니다.</div>}
      {listError && <div role="alert" style={{ padding: '16px', color: COLORS.dangerText }}>보고서 목록을 불러오지 못했습니다.</div>}
      {!loadingReports && !listError && weekKey && reports.length === 0 && (
        <div style={{ padding: '20px 0', color: COLORS.textMuted }}>이 주차에 제출된 보고서가 없습니다.</div>
      )}
      {!loadingReports && partialFailureCount > 0 && (
        <div role="alert" style={{ marginBottom: '12px', color: COLORS.warn }}>일부 보고서 상세를 불러오지 못했습니다. ({partialFailureCount}건)</div>
      )}
      {!loadingReports && orderedReports.map((report) => (
        // 바깥 감싸개 id — 본문 검색 결과로 고른 부서 카드로 스크롤할 때 찾는다.
        <div key={report.id} id={`exec-report-${report.id}`}>
          <ReportCard
            report={report}
            onRetry={loadDetail}
            onDownload={downloadFile}
            downloadingFileId={downloadingFileId}
          />
        </div>
      ))}
      {printJob && createPortal(
        <UnifiedPrintView
          key={printJob.id}
          weekLabel={printJob.label}
          reports={printJob.reports}
          onRootReady={(root) => handlePrintRoot(root, printJob)}
        />,
        document.body,
      )}
    </div>
  );
}
